import * as React from 'react'
import {createStyles, Grid, Theme, Typography, withStyles, WithStyles, withTheme, WithTheme} from "@material-ui/core";
import {inject, observer} from "mobx-react";
import UserStore from "../../stores/UserStore";
import AccountStore from "../../stores/AccountStore";
import Account from "../../model/Account"
import {observable, when} from "mobx";
import User from "../../model/User";
import Progress from "../../components/Progress";
import UserCard from "../account/UserCard";
import ControlTower, {Routes} from "../../components/ControlTower";
import SearchBar from 'material-ui-search-bar'
import {ModelUserFilterInput, UserRole, UserStatus} from "../../API";
import MarginRow from "../../components/page/MarginRow";
import Page from "../../components/page/Page";
import {RouteComponentProps} from "@reach/router";
import AccountFilter from "../../components/filter/AccountFilter";
import TitleBar from "../../components/TitleBar";
import FormValidator from "../../components/form/FormValidator";
import TextFieldValidator from "../../components/form/TextFieldValidator";

const styles = (theme: Theme) => createStyles({
  root: {
    flexGrow: 1,
    justifyContent: 'top',
    alignItems: 'center',
  },
  card: {
    width: "100%",
    paddingTop: theme.spacing(1),
  },
  controlBar: {
    display: "flex",
    flex: "none",
    flexDirection: "row",
    marginTop: theme.spacing(1),
    height: 30,
    width: "100%",
    maxWidth: 960,
    paddingRight: theme.spacing(1)
  },
  content: {
    flexGrow: 1,
    justifyContent: 'top',
    width: "100%",
    maxWidth: 960,
    marginTop: theme.spacing(0.5),
    // maxHeight: 'calc(100vh - 206px)',
    minHeight: "min-content",
    overflowY: "auto"
  },
  search: {
    flexGrow: 1,
  },
  searchBar: {
    height: 30,
  },
  filter: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "left",
    marginLeft: theme.spacing(1),
    height: 30,
  },
  filterControl: {
    textAlign: "left",
    height: 30,
  },
  userRoleFilter: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 10,
    width: 140,
    height: 30,
  },
  userStatusFilter: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 10,
    width: 160,
    height: 30,
  },
  accountFilter: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 10,
    width: 250,
    height: 30,
  },
  sortFilter: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 10,
    width: 120,
    height: 30,
  },
  actions: {
    flexGrow: 0,
    alignItems: "center",
    alignContent: "center",
    // paddingTop: 2
  },
  actionButton: {
    width: 24,
    height: 30,
    padding: 0,
    marginLeft: theme.spacing(1),
    margin: 0,
    minWidth: 64,
    color: theme.palette.secondary.contrastText,
    background: theme.palette.secondary.main,
    "&:hover": {
      background: theme.palette.secondary.dark,
    },
    "&:active": {
      background: theme.palette.secondary.light,
    },
  },
  subtitle: {
    margin: theme.spacing(0),
    paddingLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 600
  },
  quantity: {
    margin: theme.spacing(0),
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textAlign: "right",
    paddingRight: theme.spacing(1)
  },

})

enum SortOptions {
  Name = "Name",
  Created = "Created"
}

interface IPeoplePageProps {
  userStore?: UserStore
  accountStore?: AccountStore
  progress?: Progress
}

@inject("userStore", "accountStore", "progress")
@observer
class PeoplePage extends React.Component<WithStyles<typeof styles> & RouteComponentProps & IPeoplePageProps & WithTheme> {

  @observable accounts: Account[] = []
  @observable accountFilter?: Account
  @observable allUsers: User[] = []
  @observable users: User[] = []
  @observable isLoading = true
  @observable search?: string
  @observable userRoleFilter?: UserRole
  @observable userStatusFilter?: UserStatus
  @observable sortFilter = SortOptions.Name

  componentDidMount () {
    const { progress } = this.props
    this.isLoading = true
    progress!.show("PeoplePage")
    const { userStore } = this.props
    when (
      () => !userStore!.isLoading,
      async () => {
        this.search = this.getFilter("search") ?? undefined
        this.userRoleFilter = this.getFilter("userRoleFilter") ? UserRole[this.getFilter("userRoleFilter")!] : undefined
        this.userStatusFilter = this.getFilter("userStatusFilter") ? UserStatus[this.getFilter("userStatusFilter")!] : undefined
        await this.loadAccounts()
        const accountFilterId = this.getFilter("accountFilter")
        if (accountFilterId) {
          this.accountFilter = this.accounts.find((a: Account) => a.id === accountFilterId)
        }
        this.sortFilter = SortOptions[this.getFilter("sortFilter") ?? "Name"]
        this.loadUsers()
        this.isLoading = false
        progress!.hide("PeoplePage")
      }
    )
  }

  render() {
    const { classes } = this.props

    if (this.isLoading) {
      return null
    }

    const title = "People"

    return (
      <Page title={title}>
        <MarginRow>
          <div className={classes.root}>
            <TitleBar title={title}>
              <Typography variant="body1" className={classes.quantity}>{this.users.length}</Typography>
            </TitleBar>
            <FormValidator name="filterForm">
              <div className={classes.controlBar}>
                <div className={classes.search}>
                  <SearchBar value={this.search} className={classes.searchBar}
                             onChange={this.onSearchChange} onCancelSearch={this.onCancelSearch} />
                </div>

                <div className={classes.filter}>
                  <div className={classes.filterControl}>
                    <TextFieldValidator
                      type="text"
                      className={classes.userRoleFilter}
                      variant="outlined"
                      size="small"
                      name="userRoleFilter"
                      label="Role"
                      autocompleteOptions={{
                        options: [...Object.values(UserRole)],
                        value: this.userRoleFilter,
                        disableClearable: false,
                        onChange: this.onChangeUserRole
                      }}
                    />
                  </div>
                </div>

                <div className={classes.filter}>
                  <div className={classes.filterControl}>
                    <TextFieldValidator
                      type="text"
                      className={classes.userStatusFilter}
                      variant="outlined"
                      size="small"
                      name="userStatusFilter"
                      label="Status"
                      autocompleteOptions={{
                        options: [...Object.values(UserStatus)],
                        value: this.userStatusFilter,
                        disableClearable: false,
                        onChange: this.onChangeUserStatus
                      }}
                    />
                  </div>
                </div>

                <div className={classes.filter}>
                  <div className={classes.filterControl}>
                    <AccountFilter
                      className={classes.accountFilter}
                      accounts={this.accounts}
                      value={this.accountFilter}
                      onSelectAccount={this.onChangeAccount}
                      key="accountFilter"
                    />
                  </div>
                </div>

                <div className={classes.filter}>
                  <div className={classes.filterControl}>
                    <TextFieldValidator
                      type="text"
                      className={classes.sortFilter}
                      variant="outlined"
                      size="small"
                      name="sortFilter"
                      label="Sort By"
                      onChange={this.onChangeSort}
                      autocompleteOptions={{
                        options: [...Object.values(SortOptions)],
                        value: this.sortFilter,
                        disableClearable: true
                      }}
                    />
                  </div>
                </div>
              </div>
            </FormValidator>

            {this.users &&
              <Grid container className={classes.content} direction="row" spacing={1}>
                {this.users.map((u: User) => {
                  return (
                    <Grid item sm={6} xs={12} className={classes.card} key={u.id}>
                      <UserCard user={u} onCardAction={this.onCardAction}/>
                    </Grid>
                  )
                })}
              </Grid>
            }
          </div>
        </MarginRow>
      </Page>
    )
  }

  onSearchChange = (value: string) => {
    this.search = value
    this.saveFilter("search", value)
    this.filterUsers()
  }

  onCancelSearch = () => {
    this.search = undefined
    this.clearFilter("search")
    this.filterUsers()
  }

  onChangeUserStatus = (event: any, value: UserStatus, reason: any) => {
    this.userStatusFilter = value
    if (value) {
      this.saveFilter("userStatusFilter", value)
    } else {
      this.clearFilter("userStatusFilter")
    }
    this.loadUsers()
    this.filterUsers()
  }

  onChangeUserRole = (event: any, value: UserRole, reason: any) => {
    this.userRoleFilter = value
    if (value) {
      this.saveFilter("userRoleFilter", value)
    } else {
      this.clearFilter("userRoleFilter")
    }
    this.filterUsers()
  }

  onChangeAccount = (account?: Account) => {
    this.accountFilter = account
    if (account) {
      this.saveFilter("accountFilter", account.id)
    } else {
      this.clearFilter("accountFilter")
    }
    this.filterUsers()
  }

  onChangeSort = (event: any) => {
    this.sortFilter = event.target.value
    this.saveFilter(event.target.name, event.target.value)
    this.filterUsers()
  }

  onCardAction = (user: User) => {
    ControlTower.route(`${Routes.account}/${user.accountId}/person/${user.id}?ret=${Routes.people}`)
  }

  loadAccounts = async () => {
    const { accountStore } = this.props

    let accounts = await accountStore!.listAccounts()
    accounts.sort((a: Account, b: Account) => a.name.localeCompare(b.name))

    this.accounts = accounts
  }

  loadUsers = async () => {
    const { userStore } = this.props

    let filter: ModelUserFilterInput | undefined

    if (this.userStatusFilter) {
      filter = {
        userStatus: {eq: this.userStatusFilter}
      }
    }
    let users = await userStore!.listUsers(filter)
    // Assign accounts
    users.forEach((u: User) => {
      const account = this.accounts.find((a: Account) => a.id === u.accountId)
      if (account) {
        u.account = account
      }
    })
    this.allUsers = users.sort((a: User, b: User) => { return a.fullName.localeCompare(b.fullName)})
    this.filterUsers()
  }

  filterUsers = () => {
    let users: User[] = []

    const search = this.search ? this.search.toLowerCase() : ""

    this.allUsers.forEach((u: User) => {
      if (!this.accountFilter || this.accountFilter.id === u.accountId) {
        if (!this.userRoleFilter || this.userRoleFilter === u.role) {
          if (!search ||
            u.fullName.toLowerCase().indexOf(search!) >= 0 ||
            u.email.toLowerCase().indexOf(search!) >= 0) {
            users.push(u)
          }
        }
      }
    })

    if (this.sortFilter === SortOptions.Created) {
      users.sort((a: User, b: User) => b.createdAt.localeCompare(a.createdAt))
    } else {
      users.sort((a: User, b: User) => a.fullName.localeCompare(b.fullName))
    }


    this.users = users
  }

  saveFilter = (name: string, value: string) => {
    localStorage.setItem(`${Routes.people}/${name}`, value)
  }

  getFilter = (name: string) => {
    return localStorage.getItem(`${Routes.people}/${name}`)
  }

  clearFilter = (name: string) => {
    localStorage.removeItem(`${Routes.people}/${name}`)
  }

}

export default withTheme((withStyles(styles)(PeoplePage)))